<script setup lang="ts">
  import { computed, inject } from 'vue';
  import type { CompiledStaticNode, FillTitleProperties } from '@record-fill';
  import { useElementContainerStyles } from '../../model/useElementContainerStyles';
  import { useLabelPosition } from '../../model/useLabelPosition';
  import { titleChange } from './change-marks';
  import { FILL_CONTEXT_KEY } from './fill-context';

  const props = defineProps<{ node: CompiledStaticNode }>();

  const context = inject(FILL_CONTEXT_KEY, null);
  const readOnly = computed(() => context?.readOnly.value === true);

  const properties = computed(() => props.node.element.properties as FillTitleProperties);
  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(properties);
  const { containerStyles } = useElementContainerStyles(properties, props.node.type);

  const visibleLabel = computed(() => properties.value.label?.trim() ?? '');
  const placeholder = computed(() =>
    props.node.type === 'FILL_TITLE' ? properties.value.auxiliaryText : undefined,
  );
  const change = computed(() => titleChange(context, props.node));
  const changeClasses = computed(() => (change.value ? `fill-title-change fill-title-change-${change.value.tone}` : ''));
  const readOnlyFocus = computed(() =>
    readOnly.value ? { role: 'group', tabindex: -1, 'aria-label': visibleLabel.value || properties.value.name } : {},
  );
</script>

<template>
  <div
    v-if="node.visible"
    class="w-full min-w-0 max-w-full box-border text-gray-07 [font-family:var(--font-base)]"
    :class="[{ 'flex items-center': isLabelLeft }, changeClasses]"
    :style="containerStyles"
    v-bind="readOnlyFocus"
    :data-fill-field="readOnly ? node.id : undefined"
    :data-testid="`fill-title-${node.id}`"
  >
    <label
      v-if="visibleLabel"
      :for="`fill-title-input-${node.id}`"
      :class="[labelClasses, { 'shrink-0 whitespace-nowrap': isLabelLeft }]"
      :style="labelStyles"
    >
      {{ visibleLabel }}
    </label>
    <input
      :id="`fill-title-input-${node.id}`"
      type="text"
      :disabled="!readOnly"
      :readonly="readOnly"
      :tabindex="readOnly ? -1 : undefined"
      :value="node.text"
      :data-field-id="node.id"
      :placeholder="placeholder"
      :aria-label="visibleLabel || node.element.properties.name"
      :class="[
        inputClasses,
        'text-[11px] leading-4 placeholder:text-[10px] placeholder:opacity-60 disabled:cursor-not-allowed disabled:bg-[var(--color-gray-09)] disabled:opacity-60',
        { 'w-0 min-w-0 max-w-full flex-1': isLabelLeft },
      ]"
    >
  </div>
</template>

<style scoped>
  .fill-title-change-added {
    --fill-change-fill: var(--color-change-added);
    --fill-change-border: var(--color-change-added-border);
  }

  .fill-title-change-fixed {
    --fill-change-fill: var(--color-change-fixed);
    --fill-change-border: var(--color-change-fixed-border);
  }

  .fill-title-change input {
    background-color: var(--fill-change-fill);
    border-color: var(--fill-change-border);
  }
</style>
